import React, { useMemo } from 'react'
import { Pressable, ScrollView, StyleSheet, View } from 'react-native'
import { FunctionReturnType } from 'convex/server'
import { api } from '@ppwr/convex/_generated/api'
import { hasAdminRights, hasOwnerRights, type LoungeRole } from '@ppwr/convex/lounges/shared'
import { DS } from '@/design-system'
import { translate } from '../../../i18n'
import { LoungeScheduleCard } from './LoungeScheduleCard'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { LoungeCodeCard } from './LoungeCodeCard'

type LoungeGame = FunctionReturnType<typeof api.lounges.queries.getLoungeGames>[number]
type LoungeScheduledGame = FunctionReturnType<typeof api.lounges.queries.getLoungeScheduledGames>[number]

type Role = LoungeRole | null | undefined

function startTimestampOf(game: LoungeGame): number {
  return game.startedAt ?? game.pregameStartAt ?? game.createdAt
}

type CombinedGameItem =
  | { kind: 'game'; game: LoungeGame; sortKey: number }
  | { kind: 'schedule'; schedule: LoungeScheduledGame; sortKey: number }

export type LoungePlayTabProps = {
  games: LoungeGame[]
  scheduledGames: LoungeScheduledGame[]
  joinCode: string | null
  callerRole: Role
  onJoin: (gameId: string) => void
  onView: (gameId: string) => void
  onViewSchedule: (hash: string) => void
  onCreateGame: () => void
  onRotateCode: () => void
  /** Lets owners edit the join code inline (see `LoungeCodeCard`). */
  onSaveCode?: (code: string) => Promise<void>
  loungeId?: Id<'lounges'>
  rotating: boolean
}

export function LoungePlayTab({
  games,
  scheduledGames,
  joinCode,
  callerRole,
  onJoin,
  onView,
  onViewSchedule,
  onCreateGame,
  onRotateCode,
  rotating,
  onSaveCode,
  loungeId
}: LoungePlayTabProps) {
  const allGames = useMemo(() => {
    const upcoming: CombinedGameItem[] = games
      .filter((g) => g.state === 'pregame' || g.state === 'playing')
      .map((game) => ({ kind: 'game' as const, game, sortKey: startTimestampOf(game) }))

    const scheduled: CombinedGameItem[] = scheduledGames.map((schedule) => ({
      kind: 'schedule' as const,
      schedule,
      sortKey: schedule.scheduledStartTime
    }))

    return [...upcoming, ...scheduled].sort((a, b) => a.sortKey - b.sortKey)
  }, [games, scheduledGames])

  const canShare = callerRole ? hasAdminRights(callerRole) : false
  const canRotate = callerRole ? hasOwnerRights(callerRole) : false

  return (
    <ScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
      <View style={styles.section}>
        <DS.Text variant="Heading 4/Medium">{translate('loungeLobbyScreen.upcomingGames')}</DS.Text>
        {allGames.length === 0 ? (
          <View style={styles.emptyState}>
            <DS.Text variant="Body Medium/Regular" style={{ color: DS.palette['Brand/Dark Grey'] }}>
              {translate('loungeLobbyScreen.noUpcomingGames')}
            </DS.Text>
          </View>
        ) : (
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.cardsRow}>
            {allGames.map((item) => {
              if (item.kind === 'game') {
                const game = item.game
                const isLive = game.state === 'playing'
                return (
                  <LoungeScheduleCard
                    key={game._id}
                    name={game.name}
                    mode={game.mode}
                    smallBlind={game.smallBlind}
                    bigBlind={game.bigBlind}
                    maxPlayers={game.maxPlayers}
                    playerCount={game.playerCount}
                    state={game.state}
                    pregameStartAt={game.pregameStartAt}
                    startedAt={game.startedAt}
                    ctaLabel={
                      game.isJoinable ? translate('loungeLobbyScreen.join') : translate('loungeLobbyScreen.view')
                    }
                    disabled={isLive && !game.isJoinable}
                    onPress={() => (game.isJoinable ? onJoin(game.gameId) : onView(game.gameId))}
                  />
                )
              }

              const schedule = item.schedule
              // Only one-off schedules carry a sharable hash → openable via the accept-invite flow.
              // Recurring occurrences have no hash, so they render as informational (no CTA).
              const canView = schedule.hash != null
              return (
                <LoungeScheduleCard
                  key={schedule.key}
                  name={schedule.name}
                  mode={schedule.mode ?? undefined}
                  smallBlind={schedule.smallBlind ?? undefined}
                  bigBlind={schedule.bigBlind ?? undefined}
                  maxPlayers={schedule.maxPlayers ?? 9}
                  playerCount={schedule.playerCount}
                  state="pregame"
                  pregameStartAt={schedule.scheduledStartTime}
                  ctaLabel={translate('loungeLobbyScreen.view')}
                  hideCta={!canView}
                  dimmed={!schedule.isThisWeek}
                  onPress={() => {
                    if (schedule.hash) onViewSchedule(schedule.hash)
                  }}
                />
              )
            })}
          </ScrollView>
        )}
      </View>

      <Pressable
        onPress={onCreateGame}
        accessibilityRole="button"
        accessibilityLabel={translate('loungeLobbyScreen.createGame')}
        style={({ pressed }) => [styles.createGameButton, pressed && { opacity: 0.85 }]}
      >
        <DS.Icon name="Plus--enclosed" size={24} color="Brand/Purple" />
        <DS.Text variant="Heading 4/Medium" style={styles.createGameLabel}>
          {translate('loungeLobbyScreen.createGame')}
        </DS.Text>
        <DS.Icon name="Arrow---right" size={20} color="Global/Black" />
      </Pressable>

      {canShare && (
        <LoungeCodeCard
          mode="share"
          joinCode={joinCode}
          onRotateCode={onRotateCode}
          canRotate={canRotate}
          rotating={rotating}
          loungeId={loungeId}
          onSaveCode={canRotate ? onSaveCode : undefined}
        />
      )}
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  scrollContent: {
    paddingHorizontal: 24,
    paddingTop: 8,
    paddingBottom: 32,
    gap: 32
  },
  section: {
    gap: 16
  },
  cardsRow: {
    gap: 8,
    paddingRight: 24
  },
  emptyState: {
    paddingVertical: 32,
    alignItems: 'center'
  },
  createGameButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: DS.palette['Global/White'],
    borderColor: DS.palette['Brand/Light Grey'],
    borderWidth: 1,
    borderRadius: 40,
    paddingHorizontal: 16,
    paddingVertical: 12
  },
  createGameLabel: {
    flex: 1,
    fontSize: 24
  }
})
